import moment from "moment";
import { v4 } from "uuid";
import { arrayOfAssignees } from "~/schemas/schema.assignee";
import { arrayOfComments } from "~/schemas/schema.comments.jsx";
import { arrayOfProjects } from "~/schemas/schema.projects.jsx";
import { arrayOfSubtasks } from "~/schemas/schema.subtasks.jsx";
import { taskList } from "~/schemas/schema.task_lists.jsx";
import { arrayOfTaskLists } from "~/schemas/schema.task_lists.jsx";
import * as schemaTasks from "~/schemas/schema.tasks.jsx";
import * as actionTypes from "~/store/action_types";
import { getTaskById, getTaskByIdObject } from "~/store/selectors/selector.tasks.jsx";
import Angie from "~/utils/angie.jsx";
import { getDateBoundaries } from "~/utils/date";
import { fetch } from "~/utils/fetch.jsx";
import { param } from "~/utils/url_helper.jsx";
import * as trashActions from "./actions.trash.jsx";

export const addTask = (projectId, attributes, id = v4()) => {
  return {
    types: [actionTypes.ADD_TASK_LOADING, actionTypes.ADD_TASK_SUCCESS, actionTypes.ADD_TASK_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/tasks`,
        method: `post`,
        body: attributes,
      }),
    payload: {
      projectId,
      id,
      attributes,
      schema: {
        single: schemaTasks.task,
        task_list: taskList,
      },
    },
  };
};

export const addTaskFromConversation = (conversationId, attributes, id = v4()) => {
  return {
    types: [
      actionTypes.ADD_TASK_FROM_CONVERSATION_LOADING,
      actionTypes.ADD_TASK_FROM_CONVERSATION_SUCCESS,
      actionTypes.ADD_TASK_FROM_CONVERSATION_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/conversations/${conversationId}/messages/create-task`,
        method: `post`,
        body: attributes,
      }),
    payload: {
      conversationId,
      id,
      attributes,
      schema: {
        single: schemaTasks.task,
        task_list: taskList,
      },
    },
  };
};

export const addTaskFromRecurring = (projectId, attributes) => {
  return {
    types: [
      actionTypes.ADD_TASK_FROM_RECURRING_TASK_LOADING,
      actionTypes.ADD_TASK_FROM_RECURRING_TASK_SUCCESS,
      actionTypes.ADD_TASK_FROM_RECURRING_TASK_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/recurring-tasks/${attributes.id}/create-task`,
        method: `post`,
        body: attributes,
      }),
    payload: {
      projectId,
      id: attributes.id,
      attributes,
      schema: {
        single: schemaTasks.task,
        task_list: taskList,
      },
    },
  };
};

export const editTask = (projectId, taskId, attributes) => {
  return {
    types: [actionTypes.EDIT_TASK_LOADING, actionTypes.EDIT_TASK_SUCCESS, actionTypes.EDIT_TASK_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/tasks/${taskId}`,
        method: `put`,
        body: attributes,
      }),
    payload: {
      projectId,
      taskId,
      attributes,
      schema: {
        single: schemaTasks.task,
        assignees: arrayOfAssignees,
      },
    },
  };
};

export const batchEditTasks = (projectId, taskIds, attributes) => {
  return {
    types: [
      actionTypes.BATCH_EDIT_TASKS_LOADING,
      actionTypes.BATCH_EDIT_TASKS_SUCCESS,
      actionTypes.BATCH_EDIT_TASKS_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/tasks`,
        method: `put`,
        body: { attributes, task_ids: taskIds },
      }),
    payload: {
      projectId,
      taskIds,
      attributes,
      schema: schemaTasks.arrayOfTasks,
    },
  };
};

export const batchAddTasks = (projectId, attributes) => {
  return {
    types: [
      actionTypes.BATCH_ADD_TASKS_LOADING,
      actionTypes.BATCH_ADD_TASKS_SUCCESS,
      actionTypes.BATCH_ADD_TASKS_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/tasks/batch-add`,
        method: "post",
        body: attributes,
      }),
    payload: {
      projectId,
      attributes,
      schema: schemaTasks.arrayOfTasks,
    },
  };
};

// use prefetch when you want to prevent access log and clear notifications
export const loadTask = (projectId, taskId, performPrefetch = false) => {
  const url = `${Angie.api_url}/projects/${projectId}/tasks/${taskId}`;
  return {
    types: [actionTypes.LOAD_REQUEST, actionTypes.LOAD_TASK, actionTypes.LOAD_FAILURE],
    callAPI: () =>
      fetch({
        url: url,
        prefetchNext: performPrefetch,
      }),
    payload: {
      projectId,
      taskId,
      schema: {
        single: schemaTasks.task,
        comments: arrayOfComments,
        subtasks: arrayOfSubtasks,
        task_list: taskList,
        assignees: arrayOfAssignees,
      },
    },
    cache: {
      key: url,
    },
  };
};

export const fetchTasks = (projectId) => {
  const url = `${Angie.api_url}/projects/${projectId}/tasks`;
  return {
    types: [actionTypes.FETCH_TASKS_LOADING, actionTypes.FETCH_TASKS_SUCCESS, actionTypes.FETCH_TASKS_ERROR],
    callAPI: () => fetch({ url }),
    payload: {
      projectId,
      schema: {
        tasks: schemaTasks.arrayOfTasks,
      },
    },
    cache: { key: url },
  };
};

export const fetchUserTasks = (userId) => {
  return {
    types: [
      actionTypes.FETCH_USER_TASKS_LOADING,
      actionTypes.FETCH_USER_TASKS_SUCCESS,
      actionTypes.FETCH_USER_TASKS_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/users/${userId}/tasks`,
      }),
    payload: {
      userId,
      schema: {
        tasks: schemaTasks.arrayOfTasks,
        subtasks: arrayOfSubtasks,
        related: {
          Project: arrayOfProjects,
          Task: schemaTasks.arrayOfTasks,
          TaskList: arrayOfTaskLists,
        },
      },
    },
  };
};

export const completeTask = (taskId) => {
  return {
    types: [actionTypes.COMPLETE_TASK_LOADING, actionTypes.COMPLETE_TASK_SUCCESS, actionTypes.COMPLETE_TASK_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/complete/task/${taskId}`,
        method: `put`,
      }),
    payload: {
      taskId,
      schema: {
        single: schemaTasks.task,
        comments: arrayOfComments,
        subtasks: arrayOfSubtasks,
        task_list: taskList,
      },
    },
  };
};

export const openTask = (taskId) => (dispatch, getState) => {
  const state = getState();
  const task = getTaskById(state, taskId);
  return dispatch({
    types: [actionTypes.OPEN_TASK_LOADING, actionTypes.OPEN_TASK_SUCCESS, actionTypes.OPEN_TASK_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/open/task/${taskId}`,
        method: `put`,
      }),
    payload: {
      taskId,
      completedOn: task.get("completed_on"),
      schema: {
        single: schemaTasks.task,
        comments: arrayOfComments,
        subtasks: arrayOfSubtasks,
        task_list: taskList,
      },
    },
  });
};

export const moveCopyTaskToProject = (projectId, id, attributes) => {
  return {
    types: [
      actionTypes.MOVE_TASK_TO_PROJECT_LOADING,
      actionTypes.MOVE_TASK_TO_PROJECT_SUCCESS,
      actionTypes.MOVE_TASK_TO_PROJECT_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/tasks/${id}/move-to-project`,
        method: `put`,
        body: attributes,
      }),
    payload: {
      projectId,
      taskId: id,
      copy: attributes.copy,
      newProjectId: attributes.project_id,
      schema: {
        single: schemaTasks.task,
      },
    },
  };
};

export const duplicateTask = (projectId, id, attributes) => {
  return {
    types: [actionTypes.DUPLICATE_TASK_LOADING, actionTypes.DUPLICATE_TASK_SUCCESS, actionTypes.DUPLICATE_TASK_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/tasks/${id}/duplicate`,
        method: `post`,
        body: attributes,
      }),
    payload: {
      projectId,
      taskId: id,
      schema: {
        single: schemaTasks.task,
      },
    },
  };
};

export const moveToTrash = (id, trashTracking = false) => {
  const objectType = `Task`;
  return {
    ...trashActions.moveToTrash(objectType, id, {
      trash_tracking: trashTracking,
    }),
    types: [
      actionTypes.MOVE_TASK_TO_TRASH_LOADING,
      actionTypes.MOVE_TASK_TO_TRASH_SUCCESS,
      actionTypes.MOVE_TASK_TO_TRASH_ERROR,
    ],
    payload: {
      objectType,
      id,
      trash_tracking: trashTracking,
      schema: {
        single: schemaTasks.task,
      },
    },
  };
};

export const restoredFromTrash = (id) => {
  const objectType = `Task`;
  return {
    ...trashActions.restoredFromTrash(objectType, id),
    types: [
      actionTypes.RESTORE_TASK_FROM_TRASH_LOADING,
      actionTypes.RESTORE_TASK_FROM_TRASH_SUCCESS,
      actionTypes.RESTORE_TASK_FROM_TRASH_ERROR,
    ],
    payload: {
      objectType,
      id,
      schema: {
        single: schemaTasks.task,
      },
    },
  };
};

export const reorder = (projectId, sourceTaskId, targetTaskId, before = false, taskListId) => {
  const body = {
    source_task_id: sourceTaskId,
  };

  if (!targetTaskId) {
    body.target_task_list_id = taskListId;
  } else {
    body.target_task_id = targetTaskId;
    body.before = before;
  }

  return {
    types: [actionTypes.REORDER_TASKS_LOADING, actionTypes.REORDER_TASKS_SUCCESS, actionTypes.REORDER_TASKS_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/tasks/reorder`,
        method: `put`,
        body,
      }),
    payload: {
      projectId,
      sourceTaskId,
      targetTaskId,
      before,
      taskListId,
    },
  };
};

export const fetchTaskRescheduleSimulation = (taskId, attributes) => {
  let { start_on, due_on } = attributes;

  due_on = Number.isInteger(due_on) ? moment.unix(due_on).utc().format(`YYYY-MM-DD`) : due_on;
  start_on = Number.isInteger(start_on) ? moment.unix(start_on).utc().format(`YYYY-MM-DD`) : start_on;

  return {
    types: [
      actionTypes.FETCH_TASK_RESCHEDULE_SIMULATION_LOADING,
      actionTypes.FETCH_TASK_RESCHEDULE_SIMULATION_SUCCESS,
      actionTypes.FETCH_TASK_RESCHEDULE_SIMULATION_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/tasks/${taskId}/reschedule?${param({
          due_on,
          start_on,
        })}`,
      }),
    payload: {
      taskId,
      attributes,
      schema: schemaTasks.arrayOfRescheduleSimulationTasks,
    },
  };
};

export const rescheduleTask = (taskId, attributes) => {
  return {
    types: [
      actionTypes.RESCHEDULE_TASK_LOADING,
      actionTypes.RESCHEDULE_TASK_SUCCESS,
      actionTypes.RESCHEDULE_TASK_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/tasks/${taskId}/reschedule`,
        method: `post`,
        body: attributes,
      }),
    payload: {
      taskId,
      attributes,
      schema: schemaTasks.arrayOfTasks,
    },
  };
};

export const fetchPaginatedTasks = (projectId, params = {}) => {
  params = {
    ...params,
    view: params.view || "list",
  };

  const url = `${Angie.api_url}/projects/${projectId}/tasks/for-screen?${param(params)}`;

  return {
    types: [actionTypes.FETCH_TASKS_LOADING, actionTypes.FETCH_TASKS_SUCCESS, actionTypes.FETCH_TASKS_ERROR],
    callAPI: () =>
      fetch({
        url,
      }),
    payload: {
      projectId,
      schema: schemaTasks.arrayOfTasks,
    },
    cache: { key: url },
  };
};

export const fetchTasksArchiveForProject = (projectId, params = {}, filters = {}) => {
  const dateFormat = "YYYY-MM-DD";

  params = {
    ...params,
    limit: params.limit || 50,
  };

  if (filters?.taskListFilter?.length) {
    params["task_list"] = filters?.taskListFilter;
  }

  if (filters?.assigneeFilter?.length) {
    params["assignee"] = filters?.assigneeFilter;
  }

  if (filters?.dueDateFilter?.length) {
    if (filters.dueDateFilter === "not_set") {
      params["due_on_from"] = 0;
      params["due_on_to"] = 0;
    } else {
      const { from: dueOnFrom, to: dueOnTo } = getDateBoundaries(filters?.dueDateFilter);
      params["due_on_from"] = dueOnFrom.format(dateFormat);
      params["due_on_to"] = dueOnTo.format(dateFormat);
    }
  }

  if (filters?.labelFilter?.length) {
    params["label"] = filters?.labelFilter;
  }

  if (filters?.completionDateFilter?.length) {
    const { from: completedOnFrom, to: completedOnTo } = getDateBoundaries(filters?.completionDateFilter);
    params["completed_on_from"] = completedOnFrom.format(dateFormat);
    params["completed_on_to"] = completedOnTo.format(dateFormat);
  }

  if (filters?.completedByFilter?.length) {
    params["completed_by"] = filters?.completedByFilter;
  }

  const url = `${Angie.api_url}/projects/${projectId}/tasks/archive/for-screen?${param(params)}`;

  return {
    types: [
      actionTypes.FETCH_TASKS_ARCHIVE_LOADING,
      actionTypes.FETCH_TASKS_ARCHIVE_SUCCESS,
      actionTypes.FETCH_TASKS_ARCHIVE_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: url,
        withHeaders: true,
      }),
    payload: {
      projectId,
    },
  };
};

export const fetchOpenTasksForTaskList = (projectId, taskListId) => {
  return {
    types: [actionTypes.FETCH_TASKS_LOADING, actionTypes.FETCH_TASKS_SUCCESS, actionTypes.FETCH_TASKS_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/task-lists/${taskListId}/tasks`,
        method: `get`,
      }),
    payload: {
      projectId,
      taskListId,
      schema: schemaTasks.arrayOfTasks,
    },
  };
};

export const taskFromEventStream = (data = {}, type) => {
  if (data) {
    if (data.is_complete_data) {
      const { is_complete_data, ...rest } = data;
      return {
        type,
        response: rest,
        schema: schemaTasks.task,
      };
    }

    return loadTask(data.project_id, data.id, true);
  }
};

export const updateTaskFromEventStream = (data) => {
  return taskFromEventStream(data, actionTypes.UPDATE_TASK_FROM_EVENT_STREAM);
};

export const addTaskFromEventStream = (data) => {
  return taskFromEventStream(data, actionTypes.ADD_TASK_FROM_EVENT_STREAM);
};

export const completeTaskFromEventStream = (data) => {
  return taskFromEventStream(data, actionTypes.COMPLETE_TASK_FROM_EVENT_STREAM);
};

const delegateUpdateTaskFromEventStream = (data, actionType) => {
  let { project_id, id } = data;

  id = Number(id);
  project_id = Number(project_id);

  if (!isNaN(project_id) && project_id !== 0) {
    return loadTask(project_id, id, true);
  }
  return taskFromEventStream(data, actionType);
};

export const reopenTaskFromEventStream = (data) => {
  return delegateUpdateTaskFromEventStream(data, actionTypes.REOPENED_TASK_FROM_EVENT_STREAM);
};

export const taskListChangedFromEventStream = (data) => {
  return taskFromEventStream(data, actionTypes.TASK_LIST_CHANGED_FROM_EVENT_STREAM);
};

export const taskMovedToTrashFromEventStream = (data) => {
  return taskFromEventStream(data, actionTypes.TASK_MOVE_TO_TRASH_FROM_EVENT_STREAM);
};

export const taskRestoredFromTrashFromEventStream = (data) => {
  return delegateUpdateTaskFromEventStream(data, actionTypes.TASK_RESTORED_FROM_TRASH_FROM_EVENT_STREAM);
};

export const taskReorderedFromEventStream = (data) => {
  return {
    type: actionTypes.TASK_REORDERED_FROM_EVENT_STREAM,
    response: data,
  };
};

export const taskMovedToProjectFromEventStream = (data) => {
  return {
    type: actionTypes.TASK_MOVED_TO_PROJECT_FROM_EVENT_STREAM,
    response: data,
    schema: schemaTasks.task,
  };
};

export const flushAllTasksFromStore = () => {
  return {
    type: actionTypes.FLUSH_ALL_TASKS_FROM_STORE,
  };
};

export const flushTasksArchiveForProject = (projectId) => {
  return {
    type: actionTypes.FLUSH_TASKS_ARCHIVE,
    payload: {
      projectId,
    },
  };
};

export const editTaskFromWorkload = (projectId, taskId, attributes) => (dispatch, getState) => {
  const action = editTask(projectId, taskId, attributes);
  const oldTaskData = getTaskByIdObject(getState(), taskId);

  action.types = [actionTypes.WORKLOAD_EDIT_TASK_LOADING, actionTypes.EDIT_TASK_SUCCESS, actionTypes.EDIT_TASK_ERROR];

  action.payload = Object.assign({}, action.payload, { oldTaskData });

  return dispatch(action);
};

export const changeTask = (id, attributes, tasksOrder) => {
  return {
    type: actionTypes.CHANGE_TASK,
    id,
    attributes,
    tasksOrder,
  };
};
